import React, { useState, useEffect, useCallback } from 'react';
import WorkItemsTable from './WorkItemsTable';
import WorkItemDetail from './WorkItemDetail';
import TabNav from './TabNav';

const TicketList = ({ azureService }) => {
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [tabs, setTabs] = useState([]);
    const [activeTab, setActiveTab] = useState(null);
    const [refreshing, setRefreshing] = useState(false);

    // Add a validation check for the Azure service
    useEffect(() => {
        if (!azureService) {
            console.error('Azure service not provided to TicketList');
            setError('Service initialization failed');
            setLoading(false);
        }
    }, [azureService]);

    // Load column preferences with fallback
    const defaultColumns = process.env.REACT_APP_DEFAULT_COLUMNS?.split(',') ||
        ['System.Id', 'System.Title', 'System.State', 'System.AssignedTo'];

    const [selectedColumns, setSelectedColumns] = useState(() => {
        try {
            const saved = localStorage.getItem('columnPreferences');
            const parsed = saved ? JSON.parse(saved) : defaultColumns;
            console.log('Loaded column preferences:', parsed);
            return parsed;
        } catch (error) {
            console.error('Error loading column preferences:', error);
            return defaultColumns;
        }
    });

    // Load column order with error handling
    const [columnOrder, setColumnOrder] = useState(() => {
        try {
            const saved = localStorage.getItem('columnOrder');
            const parsed = saved ? JSON.parse(saved) : selectedColumns;
            console.log('Loaded column order:', parsed);
            return parsed;
        } catch (error) {
            console.error('Error loading column order:', error);
            return selectedColumns;
        }
    });

    // Save to localStorage whenever columns or order changes
    useEffect(() => {
        try {
            localStorage.setItem('columnPreferences', JSON.stringify(selectedColumns));
        } catch (error) {
            console.error('Error saving column preferences:', error);
        }
    }, [selectedColumns]);

    useEffect(() => {
        try {
            localStorage.setItem('columnOrder', JSON.stringify(columnOrder));
        } catch (error) {
            console.error('Error saving column order:', error);
        }
    }, [columnOrder]);

    // Initialize tabs from environment variable
    useEffect(() => {
        try {
            console.log('Initializing tabs...');
            const queryIds = process.env.REACT_APP_QUERY_IDS || '';

            const parsedTabs = queryIds.split(',')
                .map(tab => {
                    const [id, display] = tab.split(':');
                    return { id, display };
                })
                .filter(tab => tab.id && tab.display);

            console.log('Parsed tabs:', parsedTabs);

            if (parsedTabs.length > 0) {
                setTabs(parsedTabs);
                setActiveTab(parsedTabs[0]);
            } else {
                console.error('No valid tabs configured');
                setError('No valid queries configured');
            }
        } catch (error) {
            console.error('Error initializing tabs:', error);
            setError('Failed to initialize tabs configuration');
        }
    }, []);

    const fetchTickets = useCallback(async (queryId) => {
        console.log('Fetching tickets for query:', queryId);
        try {
            setLoading(true);
            setError(null);

            if (!azureService) {
                throw new Error('Azure service is not initialized');
            }

            if (!queryId) {
                throw new Error('No query ID provided');
            }

            const workItems = await azureService.getWorkItems(queryId);
            console.log(`Fetched ${workItems.length} work items`);
            setTickets(workItems);
        } catch (err) {
            console.error('Error fetching tickets:', err);
            let errorMessage = 'Failed to fetch work items. ';

            if (err.response?.status === 401) {
                errorMessage += 'Please check your authentication and try again.';
            } else if (err.response?.status === 403) {
                errorMessage += 'You don\'t have permission to access this project or query.';
            } else if (err.response?.status === 404) {
                errorMessage += 'The specified query or project could not be found.';
            } else {
                errorMessage += err.message || 'An unexpected error occurred. Please try again later.';
            }

            setError(errorMessage);
            setTickets([]);
        } finally {
            setLoading(false);
        }
    }, [azureService]);


    // Fetch tickets when active tab changes
    useEffect(() => {
        if (activeTab && azureService) {
            console.log('Fetching tickets for tab:', activeTab);
            fetchTickets(activeTab.id);
        }
    }, [activeTab, azureService, fetchTickets]);

    // Handle column changes
    const handleColumnChange = (newColumns) => {
        console.log('Updating columns:', newColumns);
        setSelectedColumns(newColumns);

        // Update column order to include/exclude columns while maintaining order
        const newOrder = [
            ...columnOrder.filter(col => newColumns.includes(col)),
            ...newColumns.filter(col => !columnOrder.includes(col))
        ];
        setColumnOrder(newOrder);
    };

    // Handle column order changes
    const handleColumnOrderChange = (newOrder) => {
        console.log('Updating column order:', newOrder);
        setColumnOrder(newOrder);
    };

    // Handle optimistic updates for work items
    const handleWorkItemsUpdate = async (optimisticItems = null) => {
        if (optimisticItems) {
            // Apply optimistic update immediately
            setTickets(optimisticItems);
            return;
        }

        // Fallback to server refresh if no optimistic update provided or on error
        setRefreshing(true);
        try {
            if (activeTab) {
                const workItems = await azureService.getWorkItems(activeTab.id);
                setTickets(workItems);
            }
        } catch (error) {
            console.error('Error refreshing work items:', error);
            setError('Failed to refresh work items after update');
        } finally {
            setRefreshing(false);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="text-gray-600">Loading work items...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
                <p>{error}</p>
                <button
                    onClick={() => activeTab && fetchTickets(activeTab.id)}
                    className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
                >
                    Try Again
                </button>
            </div>
        );
    }

    // Add validation check for required props before rendering table
    if (!selectedColumns.length || !columnOrder.length) {
        return (
            <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded relative">
                <p>Column configuration is missing or invalid.</p>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {tabs.length > 0 && (
                <TabNav
                    tabs={tabs}
                    activeTab={activeTab}
                    onTabChange={setActiveTab}
                />
            )}

            <div className={`relative ${refreshing ? 'opacity-50' : ''}`}>
                {refreshing && (
                    <div className="absolute inset-0 bg-white bg-opacity-50 flex items-center justify-center">
                        <div className="text-gray-600">Updating...</div>
                    </div>
                )}

                <WorkItemsTable
                    workItems={tickets}
                    onSelectItem={setSelectedTicket}
                    selectedColumns={selectedColumns}
                    onColumnChange={handleColumnChange}
                    columnOrder={columnOrder}
                    onColumnOrderChange={handleColumnOrderChange}
                    azureService={azureService}
                    onWorkItemsUpdate={handleWorkItemsUpdate}
                />
            </div>

            {selectedTicket && (
                <WorkItemDetail
                    workItem={selectedTicket}
                    onClose={() => setSelectedTicket(null)}
                    azureService={azureService}
                />
            )}
        </div>
    );
};

export default TicketList;